import { useCallback, useState, useEffect } from 'react';
import { aboutApi } from '../../api/about';
import ProgressBar from '../../components/common/ProgressBar';
import { Alert, Box, Link, Typography, Stack, ToggleButton, ToggleButtonGroup } from '@mui/material';
import PageLayout from '../../components/layout/PageLayout';
import { useUserStore } from '../../stores/userStore';
import ReactQuillEditor from '../../components/Editor/ReactQuillEditor';
import ContentViewer from '../../components/common/ContentViewer';
import CancelConfirmDialog from '../../components/dialog/CancelConfirmDialog';
import BasicButton from '../../components/button/BasicButton';
import EditButton from '../../components/button/EditButton';

const hasEditorContent = (value) => value.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim().length > 0;

const PrivacyPolicy = () => {
  const isAdmin = useUserStore((state) => state.isAdmin);
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [locale, setLocale] = useState('ko');
  const [isFallback, setIsFallback] = useState(false);

  const onClickEdit = () => {
    setEditContent(isFallback ? '' : content);
    setIsEditing(true);
  };

  const handleSubmit = async () => {
    if (!hasEditorContent(editContent)) return;
    try {
      const { data } = await aboutApi.updateContent(editContent, 'PRIVACY_POLICY', locale);
      setContent(data.content);
      setIsFallback(false);
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating content:', error);
    }
  };

  const handleOnClickCancel = () => {
    setIsCancelDialogOpen(true);
  };

  const handleOnConfirmCancel = () => {
    setIsCancelDialogOpen(false);
    setIsEditing(false);
  };

  const fetchContent = useCallback(async () => {
    try {
      const { data } = await aboutApi.getContent('PRIVACY_POLICY', locale);
      setContent(data.content);
      setIsFallback(Boolean(data.fallback));
    } catch (error) {
      console.error('Error fetching content:', error);
    } finally {
      setLoading(false);
    }
  }, [locale]);

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  const handleLocaleChange = (_event, nextLocale) => {
    if (!nextLocale || nextLocale === locale) return;
    setIsEditing(false);
    setEditContent('');
    setLocale(nextLocale);
    setLoading(true);
  };

  // 전역 복사 방지 이벤트 리스너 (편집 모드가 아닐 때만 적용)
  useEffect(() => {
    if (isEditing) return; // 편집 모드일 때는 복사 방지 비활성화

    const preventCopy = (e) => {
      e.preventDefault();
      return false;
    };

    const preventSelect = (e) => {
      e.preventDefault();
      return false;
    };

    const preventContextMenu = (e) => {
      e.preventDefault();
      return false;
    };

    const preventKeyDown = (e) => {
      // Ctrl+C, Ctrl+A, Ctrl+X, Ctrl+V, Ctrl+Z, Ctrl+Y 차단
      if (
        (e.ctrlKey || e.metaKey) &&
        (e.key === 'c' ||
          e.key === 'a' ||
          e.key === 'x' ||
          e.key === 'v' ||
          e.key === 'z' ||
          e.key === 'y')
      ) {
        e.preventDefault();
        return false;
      }
      // F12 키 차단
      if (e.key === 'F12') {
        e.preventDefault();
        return false;
      }
    };

    // 전역 이벤트 리스너 추가
    document.addEventListener('copy', preventCopy);
    document.addEventListener('selectstart', preventSelect);
    document.addEventListener('contextmenu', preventContextMenu);
    document.addEventListener('keydown', preventKeyDown);

    // 컴포넌트 언마운트 시 이벤트 리스너 제거
    return () => {
      document.removeEventListener('copy', preventCopy);
      document.removeEventListener('selectstart', preventSelect);
      document.removeEventListener('contextmenu', preventContextMenu);
      document.removeEventListener('keydown', preventKeyDown);
    };
  }, [isEditing]);

  if (isEditing) {
    return (
      <PageLayout>
        <ProgressBar isLoading={loading}>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">편집 언어: {locale === 'en' ? 'English' : '한국어'}</Typography>
            {locale === 'en' && isFallback && (
              <Alert severity="info">영문 개인정보처리방침이 아직 없습니다. 아래 편집기는 빈 영문 문서로 시작하며 한국어 원문은 변경하지 않습니다.</Alert>
            )}
          <Box>
            <ReactQuillEditor value={editContent} onChange={(value) => setEditContent(value)} />
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: 1,
              mt: { xs: '30px', lg: '50px' },
              width: { xs: '100%', md: 'auto' },
            }}
          >
            <BasicButton
              fullWidth
              variant="outlined"
              onClick={handleOnClickCancel}
              sx={{ width: { md: '110px' }, height: '48px' }}
            >
              취소
            </BasicButton>
            <BasicButton
              fullWidth
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={!hasEditorContent(editContent)}
              sx={{ width: { md: '110px' }, height: '48px' }}
            >
              등록
            </BasicButton>
          </Box>
          </Stack>
        </ProgressBar>
        <CancelConfirmDialog
          open={isCancelDialogOpen}
          onClose={() => setIsCancelDialogOpen(false)}
          onConfirm={handleOnConfirmCancel}
        />
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <ProgressBar isLoading={loading}>
        <Stack spacing={'30px'}>
          {/* 제목 */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              pb: { lg: 2, md: 0, xs: 0 },
            }}
          >
            <Typography fontSize={{ lg: 28, md: 22, xs: 20 }} fontWeight="bold">
              개인정보처리방침
            </Typography>
            {isAdmin && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1 }}>
                <ToggleButtonGroup value={locale} exclusive onChange={handleLocaleChange} size="small">
                  <ToggleButton value="ko">한국어</ToggleButton>
                  <ToggleButton value="en">English</ToggleButton>
                </ToggleButtonGroup>
                <EditButton onToggle={onClickEdit} />
              </Box>
            )}
          </Box>

          {isAdmin && locale === 'en' && isFallback && (
            <Alert severity="info">공식 영문본이 없어 한국어 원문을 미리보기로 표시하고 있습니다. 편집을 누르면 빈 영문 문서에서 시작합니다.</Alert>
          )}

          <Box
            className="no-copy"
            sx={{
              userSelect: 'none',
              WebkitUserSelect: 'none',
              MozUserSelect: 'none',
              msUserSelect: 'none',
              cursor: 'default',
            }}
          >
            <ContentViewer text={content} />
          </Box>
          <Box component="section" aria-labelledby="account-rights-heading">
            <Typography
              id="account-rights-heading"
              component="h2"
              fontSize={18}
              fontWeight={700}
              mb={1}
            >
              {locale === 'en' ? 'Account deletion and your rights' : '계정 삭제·권리 행사'}
            </Typography>
            <Link href={locale === 'en' ? '/account-deletion?lang=en' : '/account-deletion'}>
              {locale === 'en'
                ? 'Delete your Postpop account and data'
                : '포스트팝 계정 및 데이터 삭제 안내'}
            </Link>
          </Box>
        </Stack>
      </ProgressBar>
    </PageLayout>
  );
};

export default PrivacyPolicy;
